import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import { DataTableItem, DataTableRow, Icon, InputSwitch } from "../Component";
import ConfirmationModal from "../modals/ConfirmationModal";

import vacancyService from "../../services/vacancyService";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import EventEmitter, { EVENT_TOPICS } from "../../utilities/eventUtils";
import {
  DATE_FORMATS,
  LANGUAGE_KEYS,
  VACANCY_TYPES_READABLE,
} from "../../utilities/constants";
import { formatDate } from "../../utilities/dateTimeUtils";
import {
  buildVacancyBlockRequestBody,
  richTextToPlainText,
} from "../../utilities/vacancyUtils";

const VacanciesPageTableBodyItem = ({ item }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [onFetch, isLoading] = useBeManualFetcher();
  const [onToggleFetch, isToggleLoading] = useBeManualFetcher();

  // Mavjud vakansiyalarda isActive null bo'lishi mumkin — faqat true aktiv.
  const [isActive, setIsActive] = useState(item?.isActive === true);
  const [pendingActive, setPendingActive] = useState(null);
  // InputSwitch uncontrolled: defaultChecked faqat mount paytida qo'llanadi.
  // Nonce o'zgarganda qayta mount bo'lib, ko'rinish holatga moslashadi
  // (bekor qilinganda yoki so'rov xato bo'lganda kerak).
  const [switchNonce, setSwitchNonce] = useState(0);
  const resyncSwitch = () => setSwitchNonce((nonce) => nonce + 1);

  const handleVacancyDelete = async (id) => {
    await onFetch({
      action: () => vacancyService.removeVacancy(id),
      onLoad: () => {
        EventEmitter.emit(EVENT_TOPICS.ON_VACANCY_DELETED, {
          deletedVacancyId: id,
        });

        setShowDeleteModal(false);
      },
      successMessage: "Vacancy successfully deleted",
    });
  };

  const handleActiveConfirm = () => {
    const nextValue = pendingActive;
    const previousValue = isActive;

    setIsActive(nextValue);

    onToggleFetch({
      // PUT butun obyektni kutadi: qolgan maydonlar serverdagi holicha ketadi.
      action: () =>
        vacancyService.updateVacancy(
          item?.id,
          buildVacancyBlockRequestBody(item, { isActive: nextValue })
        ),
      onLoad: () => setPendingActive(null),
      actionWhenError: () => {
        setIsActive(previousValue);
        resyncSwitch();
        setPendingActive(null);
      },
      successMessage: nextValue
        ? "Vacancy activated"
        : "Vacancy deactivated",
    });
  };

  const handleActiveCancel = () => {
    setPendingActive(null);
    resyncSwitch();
  };

  const handleRowClick = (e) => {
    if (!e.currentTarget.contains(e.target)) return;
    if (e.target.closest("input, label, a, button, .dropdown")) return;

    navigate(`/vacancies/${item?.id}`, { state: item });
  };

  return (
    <DataTableItem style={{ cursor: "pointer" }} onClick={handleRowClick}>
      <DataTableRow noText>{item?.orderIndex ?? "-"}</DataTableRow>
      <DataTableRow noText>
        <Link to={`/vacancies/${item?.id}`} state={item}>
          <span className="tb-lead">{item?.jobTitle?.[LANGUAGE_KEYS.EN]}</span>
        </Link>
      </DataTableRow>
      <DataTableRow
        text={
          VACANCY_TYPES_READABLE[item?.vacancyType] ??
          item?.vacancyType ??
          "-"
        }
      />
      <DataTableRow
        text={richTextToPlainText(item?.deadline?.[LANGUAGE_KEYS.EN]) || "-"}
      />
      <DataTableRow
        size="sm"
        text={formatDate(item?.createdAt, DATE_FORMATS.DATE_REGISTERED)}
      />

      <DataTableRow noText>
        <InputSwitch
          key={`active-${isActive}-${switchNonce}`}
          id={`${item?.id}-active`}
          checked={isActive}
          onClick={() => setPendingActive(!isActive)}
        />
      </DataTableRow>

      <DataTableRow className="nk-tb-col-tools" noText>
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="dropdown-toggle btn btn-icon btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#delete"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDeleteModal(true);
                      }}
                    >
                      <Icon name="trash"></Icon>
                      <span>Delete</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      </DataTableRow>

      <ConfirmationModal
        showModal={pendingActive !== null}
        onClose={handleActiveCancel}
        onConfirm={handleActiveConfirm}
        modalTitle={
          pendingActive ? "Activate this vacancy?" : "Deactivate this vacancy?"
        }
        confirmTitle="Yes"
        confirmColor={pendingActive ? "primary" : "danger"}
        isLoading={isToggleLoading}
      />
      <ConfirmationModal
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleVacancyDelete(item?.id)}
        confirmColor="danger"
        confirmTitle="Delete"
        isLoading={isLoading}
      />
    </DataTableItem>
  );
};

export default VacanciesPageTableBodyItem;
